import React from "react";
import * as XLSX from "xlsx-js-style";
import SearchNomina from "../../components/SearchNomina.tsx";
import PayrollHeader from "./PayrollHeader.tsx";
import { notification } from "../../utils/Notifications.tsx";
import {
  findPayrollSummary,
  findPayrollRowDetailsBatch,
  isPayrollFetchAborted,
  type PayrollRowDetailsSnapshot,
} from "../../services/payroll.service.ts";
import EmptyImage from "../../components/EmptyImage.tsx";
import { IPayrollReport, PayrollReport } from "../../models/Payroll.ts";
import { PaymentDetail } from "../../models/PaymentDetail.ts";
import RowPayRoll, { type CuadroMandoTableRowSnapshot } from "./RowPayRoll.tsx";
import {
  CuadroMandoLoadToast,
  type CuadroMandoLoadToastPhase,
} from "../../components/nomina/CuadroMandoLoadToast.tsx";
import { CuadroMandoTableLoading } from "../../components/nomina/CuadroMandoTableLoading.tsx";
import dayjs from "dayjs";
import { parseCurrency, getCurrencyFormat } from "../../utils/index.ts";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/index.ts";
import {
  setIncentives,
  setRecords,
  setTotal,
  setPage,
  updateRecordByIndex,
} from "../../store/slices/payrollSlice.ts";
import {
  processFormattedChecks,
  getPartnerHoursByDate,
  getCuadroDeductionColumnsForExport,
} from "../../utils/payrollUtils.ts";
import PaginationPayroll from "../../components/payroll/PaginationPayroll.tsx";
import { DatePicker } from "antd";
import locale from "antd/es/date-picker/locale/es_ES";
import { autocompletePartners, readPartnersByBranch } from "../../services/partner.service.ts";
import ModalContainer from "../../components/ModalContainer.tsx";
import { SpecialPartner } from "./CuadroMando.tsx";
import { useFullDayHours } from "../../hooks/useFullDayHours.tsx";
import { usePayrollBranchMeta } from "../../hooks/usePayrollBranchMeta.ts";
import {
  findIncidentPartnerCountsBatch,
  type PartnerIncidentCountSnapshot,
} from "../../services/incident.service.ts";
const { RangePicker } = DatePicker;

const normalizeSearchText = (text: string): string => {
  if (!text) return "";

  return text
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
};

function parseSummaryNumber(value: unknown): number {
  if (typeof value === "number" && !Number.isNaN(value)) return value;
  return parseFloat(String(value ?? "").replace(/[^0-9.-]+/g, "")) || 0;
}

function mapApiPayrollToRecord(
  record: any,
  index: number,
  previous?: IPayrollReport
): IPayrollReport {
  const dailySalaryNum = parseSummaryNumber(record.dailySalary);
  const hourlyPayNum = parseSummaryNumber(record.hourlyPay);
  const salaryPerFortnightNum = parseSummaryNumber(record.salaryPerFortnight);
  const totalIncentivesNum = parseSummaryNumber(record.totalIncentives);
  const deductionColumns = getCuadroDeductionColumnsForExport({
    totalDeductions: record.totalDeductions,
    tarjetasDeductions: record.tarjetasDeductions,
    totalDeductionsExcludingTarjetas: record.totalDeductionsExcludingTarjetas,
    calculatedNewDeductions: record.calculatedNewDeductions,
  });
  const totalDeductionsExcludingTarjetasNum = parseSummaryNumber(
    deductionColumns.totalDeductions
  );
  const tarjetasDeductionsNum = parseSummaryNumber(deductionColumns.tarjetas);
  const totalPayNum = parseSummaryNumber(record.totalPayWithIncentives);
  const celebrityPayNum = parseSummaryNumber(record.celebrityDaysPayTotal);
  const uniformDeductionNum = parseSummaryNumber(record.uniformDeduction);
  const extraHoursNum = parseSummaryNumber(record.extraHours);
  const authorizedHoursNum = parseSummaryNumber(record.authorizedHours);
  const totalHoursWorkedNum = parseSummaryNumber(record.totalHoursWorked);

  const paymentDetails: PaymentDetail =
    record.paymentDetails ??
    previous?.paymentDetails ?? {
      totalPay: `$${totalPayNum.toFixed(2)}`,
      totalPayNumber: totalPayNum,
      hoursAuthorizedPay: `$${totalPayNum.toFixed(2)}`,
      doubleShiftPayTotal: "$0.00",
      extraHourPayTotal: "$0.00",
      freeDaysEnjoyedPay: "$0.00",
      freeDayWorkedPayTotal: "$0.00",
      hourlyPay: hourlyPayNum,
      hoursAuthorized: authorizedHoursNum,
      extraHours: extraHoursNum,
      doubleShiftsHours: 0,
      freeDaysEnjoyed: record.freeDaysEnjoyed || 0,
      freeDaysWorked: record.freeDaysWorked || 0,
      hoursFreeDaysWorked: 0,
      worked: totalHoursWorkedNum,
      hoursWorkedRounded: totalHoursWorkedNum,
      extraHoursRounded: extraHoursNum,
      workedRounded: authorizedHoursNum,
    };

  const paymentLabel =
    typeof record.totalPayWithIncentives === "number"
      ? `$${record.totalPayWithIncentives.toFixed(2)}`
      : String(record.totalPayWithIncentives ?? previous?.payment ?? "$0.00");

  return {
    id: previous?.id ?? `payroll-${record.partnerId}`,
    partnerId: record.partnerId,
    showNewHireChip: record.showNewHireChip === true,
    payrollRecruitmentDate: record.payrollRecruitmentDate ?? null,
    payrollPeriodRange: record.payrollPeriodRange ?? null,
    name: record.name,
    position: record.position,
    branch: record.branch,
    salary: record.salary,
    dailySalary: `$${dailySalaryNum.toFixed(2)}`,
    payPerHour: `$${hourlyPayNum.toFixed(2)}`,
    salaryPerFortnight: `$${salaryPerFortnightNum.toFixed(2)}`,
    daysWorked: record.daysWorked,
    jornadasCompletas: record.jornadasCompletas || 0,
    totalHoursWorked: record.totalHoursWorked,
    doubleShifts: record.doubleShifts,
    extraHours: record.extraHours,
    freeDaysWorked: record.freeDaysWorked || 0,
    totalIncentives: `$${totalIncentivesNum.toFixed(2)}`,
    totalDeductions: `$${totalDeductionsExcludingTarjetasNum.toFixed(2)}`,
    tarjetasDeductions: `$${tarjetasDeductionsNum.toFixed(2)}`,
    totalDeductionsExcludingTarjetas: `$${totalDeductionsExcludingTarjetasNum.toFixed(2)}`,
    totalIncidents: record.totalIncidents,
    totalPayWithIncentives: `$${totalPayNum.toFixed(2)}`,
    NormalCalculationNomina: record.NormalCalculationNomina,
    authorizedHours: record.authorizedHours,
    freeDaysEnjoyed: record.freeDaysEnjoyed || 0,
    celebrityDaysWorked: record.celebrityDaysWorked || 0,
    celebrityDaysPayTotal:
      celebrityPayNum > 0 ? `$${celebrityPayNum.toFixed(2)}` : "$0.00",
    uniformDeduction:
      uniformDeductionNum > 0 ? `$${uniformDeductionNum.toFixed(2)}` : undefined,
    payment: paymentLabel,
    periodicity_name:
      record.periodicity_name ?? previous?.periodicity_name ?? "",
    totalHollydays: Number(
      record.totalHollydays ?? previous?.totalHollydays ?? 0
    ),
    workedRounded: Number(
      paymentDetails.workedRounded ??
        record.workedRounded ??
        previous?.workedRounded ??
        0
    ),
    paymentDetails,
    calculatedBonuses: record.calculatedBonuses || [],
    calculatedNewDeductions: record.calculatedNewDeductions || [],
    calculatedMissingBoxes: record.calculatedMissingBoxes || [],
    snapshotDetail: record.snapshotDetail,
    schedules: previous?.schedules ?? [],
    schedulesIds: previous?.schedulesIds ?? [],
    realSchedule: previous?.realSchedule ?? [],
    index,
  };
}

const PayrollBranch = ({
  branch,
  dates,
  specialPartners,
  onAddSpecialPartner,
  onRemoveSpecialPartner,
  onCuadroExportSnapshot,
}: {
  branch: number;
  dates: { start: dayjs.Dayjs; end: dayjs.Dayjs };
  specialPartners?: SpecialPartner[];
  onAddSpecialPartner?: (sp: SpecialPartner) => void;
  onRemoveSpecialPartner?: (partnerId: number) => void;
  onCuadroExportSnapshot?: (
    partnerId: number,
    snapshot: CuadroMandoTableRowSnapshot | null
  ) => void;
}) => {
  const dispatch = useDispatch();
  const { fullDayHours } = useFullDayHours(branch);
  const { refetch, records, page, limit, total } = useSelector(
    (state: RootState) => state.payroll
  );
  const [loading, setLoading] = React.useState(false);
  const [detailedRecords, setDetailedRecords] = React.useState<any[]>([]);
  const [realCheckRecords, setRealCheckRecords] = React.useState<any[]>([]);
  const payrollBranchMeta = usePayrollBranchMeta(realCheckRecords, branch);
  const [search, setSearch] = React.useState("");
  const [isPending, startTransition] = React.useTransition();
  const deferredSearch = React.useDeferredValue(search);

  const filteredRecords = records;
  const [sort, setSort] = React.useState({
    column: "Chilito",
    order: "asc",
  });
  const fetchAbortRef = React.useRef<AbortController | null>(null);
  const incidentBatchAbortRef = React.useRef<AbortController | null>(null);
  const rowDetailsBatchAbortRef = React.useRef<AbortController | null>(null);
  const [incidentCountsByPartnerId, setIncidentCountsByPartnerId] =
    React.useState<Record<number, PartnerIncidentCountSnapshot>>({});
  const [rowDetailsByPartnerId, setRowDetailsByPartnerId] = React.useState<
    Record<number, PayrollRowDetailsSnapshot>
  >({});
  /** false = aún no termina el batch; las filas NO deben pedir payment-detail/chart solas. */
  const [rowDetailsBatchSettled, setRowDetailsBatchSettled] =
    React.useState(true);
  const [incidentBatchPending, setIncidentBatchPending] =
    React.useState(false);
  const [loadToastPhase, setLoadToastPhase] =
    React.useState<CuadroMandoLoadToastPhase>(null);

  const [showAddSpecial, setShowAddSpecial] = React.useState(false);
  const [specialSearchTerm, setSpecialSearchTerm] = React.useState("");
  const [specialSearchResults, setSpecialSearchResults] = React.useState<any[]>([]);
  const [specialSelectedPartner, setSpecialSelectedPartner] = React.useState<any>(null);
  const [specialDates, setSpecialDates] = React.useState<{ start: dayjs.Dayjs; end: dayjs.Dayjs } | null>(null);
  const [addingSpecial, setAddingSpecial] = React.useState(false);
  const [showSpecialDropdown, setShowSpecialDropdown] = React.useState(false);
  const [newPartnerIds, setNewPartnerIds] = React.useState<Set<number>>(new Set());
  const specialSearchTimerRef = React.useRef<any>(null);
  const specialInputContainerRef = React.useRef<HTMLDivElement>(null);
  const [specialDropdownPos, setSpecialDropdownPos] = React.useState({ top: 0, left: 0, width: 0 });

  const toTitleCase = (str: string) =>
    str.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

  const normalizeStr = (str: string) =>
    str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

  const getPartnerDisplayName = (partner: any) =>
    toTitleCase(
      `${partner.person.firstName} ${partner.person.lastName} ${partner.person.secondLastName || ""}`.trim()
    );

  const updateSpecialDropdownPos = () => {
    if (specialInputContainerRef.current) {
      const rect = specialInputContainerRef.current.getBoundingClientRect();
      setSpecialDropdownPos({ top: rect.bottom, left: rect.left, width: rect.width });
    }
  };

  const safeToFixed = (value: any, decimals: number = 2): string => {
    if (value === null || value === undefined) return "0.00";
    const num = typeof value === "number" ? value : parseFloat(value);
    return isNaN(num) ? "0.00" : num.toFixed(decimals);
  };

  const exportTableToExcel = () => {
    if (filteredRecords.length === 0) {
      notification("Aviso", "No hay datos para exportar", "warning");
      return;
    }

    const workbook = XLSX.utils.book_new();

    const headers = [
      "Chilito",
      "Sueldo base 15",
      "Salario diario",
      "Pago por hora",
      "Días laborados",
      "Jornadas completadas",
      "Horas trabajadas",
      "Horas normales",
      "Turnos dobles",
      "Horas extras",
      "Descanso trabajado",
      "Descanso disfrutado",
      "Días festivos",
      "Bonos",
      "Total deducciones",
      "Tarjetas",
      "Incidencias",
      "Pago estimado",
    ];

    const data: string[][] = [headers];

    const allRecords: IPayrollReport[] = [
      ...filteredRecords,
      ...(specialPartners || []).map((sp) => sp.record as IPayrollReport),
    ];

    allRecords.forEach((record: IPayrollReport) => {
      const deductions = getCuadroDeductionColumnsForExport(record);
      const row: string[] = [
        record.name || "",
        record.salaryPerFortnight || "",
        record.dailySalary || "",
        record.payPerHour || "",
        String(record.daysWorked ?? 0),
        String(record.jornadasCompletas ?? 0),
        safeToFixed(record.totalHoursWorked, 1),
        safeToFixed(record.authorizedHours, 1),
        String(record.doubleShifts ?? 0),
        safeToFixed(record.extraHours, 1),
        String(record.freeDaysWorked ?? 0),
        String(record.freeDaysEnjoyed ?? 0),
        String(record.celebrityDaysWorked ?? 0),
        record.totalIncentives || "$0.00",
        deductions.totalDeductions,
        deductions.tarjetas,
        String(record.totalIncidents ?? 0),
        record.totalPayWithIncentives || "$0.00",
      ];
      data.push(row);
    });

    const worksheet = XLSX.utils.aoa_to_sheet(data);

    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "50B7C0" } },
      alignment: { horizontal: "center", vertical: "center" },
    };

    for (let i = 0; i < headers.length; i++) {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: i });
      if (worksheet[cellRef]) {
        worksheet[cellRef].s = headerStyle;
      }
    }

    worksheet["!cols"] = headers.map((h) => ({
      wch: Math.max(h.length + 2, 15),
    }));

    XLSX.utils.book_append_sheet(workbook, worksheet, "Cuadro de Mando");

    const startDate = dates.start.format("DD-MM-YYYY");
    const endDate = dates.end.format("DD-MM-YYYY");
    const fileName = `Cuadro_Mando_${startDate}_${endDate}.xlsx`;

    XLSX.writeFile(workbook, fileName);
    notification("Éxito", "Excel descargado correctamente", "success");
  };

  const refreshSinglePartner = React.useCallback(
    async (partnerId: number) => {
      try {
        const startDate = dates.start.format("YYYY-MM-DD");
        const endDate = dates.end.format("YYYY-MM-DD");

        const currentPartner = records.find((r) => r.partnerId === partnerId);
        if (!currentPartner) {
          console.warn(`Partner ${partnerId} not found in current records`);
          return;
        }

        const [summaryResponse, rowDetailsMap, incidentCountsMap] =
          await Promise.all([
            findPayrollSummary(
              Number(branch),
              startDate,
              endDate,
              100,
              0,
              "",
              sort.column,
              sort.order,
              undefined,
              [partnerId]
            ),
            findPayrollRowDetailsBatch(
              Number(branch),
              startDate,
              endDate,
              [partnerId],
              undefined,
              { refresh: true }
            ),
            findIncidentPartnerCountsBatch(
              Number(branch),
              startDate,
              endDate,
              [partnerId]
            ),
          ]);

        if (rowDetailsMap && Object.keys(rowDetailsMap).length > 0) {
          setRowDetailsByPartnerId((prev) => ({ ...prev, ...rowDetailsMap }));
        }

        const incidentSnapshot = incidentCountsMap[partnerId];
        if (incidentSnapshot) {
          setIncidentCountsByPartnerId((prev) => ({
            ...prev,
            [partnerId]: incidentSnapshot,
          }));
        }

        const updatedPartner = summaryResponse?.payroll?.find(
          (p: any) => p.partnerId === partnerId
        );

        if (updatedPartner) {
          dispatch(
            updateRecordByIndex({
              index: currentPartner.index,
              data: mapApiPayrollToRecord(
                updatedPartner,
                currentPartner.index,
                currentPartner
              ),
            })
          );
        } else {
          console.warn(
            `refreshSinglePartner: sin fila en summary para partner ${partnerId}`
          );
          notification(
            "Aviso",
            "No se pudo refrescar la fila del cuadro; recarga la página o cambia de página.",
            "warning"
          );
        }
      } catch (error) {
        console.error("Error refreshing partner:", error);
        notification(
          "Error ⚠️",
          "Error al actualizar los datos del colaborador",
          "danger"
        );
      }
    },
    [branch, dates, records, dispatch, sort.column, sort.order]
  );

  const handleSpecialPartnerSearch = async (term: string) => {
    setSpecialSearchTerm(term);
    setSpecialSelectedPartner(null);
    if (!term.trim()) {
      setSpecialSearchResults([]);
      setShowSpecialDropdown(false);
      return;
    }
    if (specialSearchTimerRef.current) clearTimeout(specialSearchTimerRef.current);
    specialSearchTimerRef.current = setTimeout(async () => {
      try {
        const normalized = normalizeStr(term);
        const results = await autocompletePartners(normalized, branch);
        setSpecialSearchResults(results || []);
        updateSpecialDropdownPos();
        setShowSpecialDropdown(true);
      } catch {
        setSpecialSearchResults([]);
      }
    }, 300);
  };

  const handleSpecialPartnerFocus = async () => {
    if (specialSelectedPartner) return;
    if (specialSearchTerm.trim() && specialSearchResults.length > 0) {
      updateSpecialDropdownPos();
      setShowSpecialDropdown(true);
      return;
    }
    try {
      const allPartners: any[] = (await readPartnersByBranch(branch)) || [];
      const startOfMonth = dayjs().startOf("month");
      const recentPartners = allPartners.filter((p: any) => {
        const createdAt = p.createdAt || p.candidate?.createdAt;
        return createdAt ? dayjs(createdAt).isAfter(startOfMonth) : false;
      });
      const defaultList =
        recentPartners.length > 0 ? recentPartners : allPartners.slice(0, 10);
      setNewPartnerIds(new Set(recentPartners.map((p: any) => p.id)));
      setSpecialSearchResults(defaultList);
      updateSpecialDropdownPos();
      setShowSpecialDropdown(true);
    } catch {
      setSpecialSearchResults([]);
    }
  };

  const handleAddSpecialPartner = async () => {
    if (!specialSelectedPartner || !specialDates || !onAddSpecialPartner) return;
    setAddingSpecial(true);
    try {
      const startDate = specialDates.start.format("YYYY-MM-DD");
      const endDate = specialDates.end.format("YYYY-MM-DD");
      const response = await findPayrollSummary(
        Number(branch),
        startDate,
        endDate,
        100,
        0,
        ""
      );
      const rawRecord = (response?.payroll || []).find(
        (p: any) => p.partnerId === specialSelectedPartner.id
      );
      if (!rawRecord) {
        notification("Aviso", "No se encontraron registros para ese colaborador en esas fechas", "warning");
        return;
      }
      const mappedRecord = {
        partnerId: rawRecord.partnerId,
        showNewHireChip: rawRecord.showNewHireChip === true,
        payrollRecruitmentDate: rawRecord.payrollRecruitmentDate ?? null,
        payrollPeriodRange: rawRecord.payrollPeriodRange ?? null,
        name: rawRecord.name,
        position: rawRecord.position,
        branch: rawRecord.branch,
        salary: rawRecord.salary,
        dailySalary: `$${rawRecord.dailySalary.toFixed(2)}`,
        payPerHour: `$${rawRecord.hourlyPay.toFixed(2)}`,
        salaryPerFortnight: `$${rawRecord.salaryPerFortnight.toFixed(2)}`,
        daysWorked: rawRecord.daysWorked,
        jornadasCompletas: rawRecord.jornadasCompletas || 0,
        totalHoursWorked: rawRecord.totalHoursWorked,
        doubleShifts: rawRecord.doubleShifts,
        extraHours: rawRecord.extraHours,
        freeDaysWorked: rawRecord.freeDaysWorked || 0,
        freeDaysEnjoyed: rawRecord.freeDaysEnjoyed || 0,
        celebrityDaysWorked: rawRecord.celebrityDaysWorked || 0,
        totalIncentives: `$${rawRecord.totalIncentives.toFixed(2)}`,
        totalDeductions: `$${(
          rawRecord.totalDeductionsExcludingTarjetas ??
          Math.max(
            0,
            rawRecord.totalDeductions - (rawRecord.tarjetasDeductions ?? 0)
          )
        ).toFixed(2)}`,
        tarjetasDeductions: `$${(rawRecord.tarjetasDeductions ?? 0).toFixed(2)}`,
        totalDeductionsExcludingTarjetas: `$${(
          rawRecord.totalDeductionsExcludingTarjetas ??
          Math.max(
            0,
            rawRecord.totalDeductions - (rawRecord.tarjetasDeductions ?? 0)
          )
        ).toFixed(2)}`,
        totalIncidents: rawRecord.totalIncidents,
        totalPayWithIncentives: `$${rawRecord.totalPayWithIncentives.toFixed(2)}`,
        NormalCalculationNomina: rawRecord.NormalCalculationNomina,
        authorizedHours: rawRecord.authorizedHours,
        uniformDeduction: rawRecord.uniformDeduction ? `$${rawRecord.uniformDeduction.toFixed(2)}` : undefined,
        paymentDetails: rawRecord.paymentDetails || { totalPayNumber: rawRecord.totalPayWithIncentives || 0, doubleShiftsHours: 0, hoursFreeDaysWorked: 0, freeDaysWorked: 0, freeDaysEnjoyed: 0, extraHours: 0, totalIncentives: 0, totalDeductions: 0 },
        calculatedBonuses: rawRecord.calculatedBonuses || [],
        calculatedNewDeductions: rawRecord.calculatedNewDeductions || [],
        calculatedMissingBoxes: rawRecord.calculatedMissingBoxes || [],
        schedules: [],
        schedulesIds: [],
        realSchedule: [],
        index: -1,
      };
      onAddSpecialPartner({ record: mappedRecord, rawRecord, customDates: specialDates });
      setShowAddSpecial(false);
      setSpecialSearchTerm("");
      setSpecialSelectedPartner(null);
      setSpecialDates(null);
      notification("Éxito", `${rawRecord.name} agregado con fechas especiales`, "success");
    } catch (error) {
      notification("Error", "No se pudo agregar el colaborador especial", "danger");
    } finally {
      setAddingSpecial(false);
    }
  };

  const fetchData = async () => {
    fetchAbortRef.current?.abort();
    const controller = new AbortController();
    fetchAbortRef.current = controller;

    setLoading(true);
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    try {
      const startDate = dates.start.format("YYYY-MM-DD");
      const endDate = dates.end.format("YYYY-MM-DD");

      console.log(
        `Fetching payroll data for branch ${branch} from ${startDate} to ${endDate}`
      );

      timeoutId = setTimeout(() => {
        controller.abort();
      }, 30000);

      const offset = page * limit;
      const normalizedSearch = normalizeSearchText(deferredSearch || "");
      const response = await findPayrollSummary(
        Number(branch),
        startDate,
        endDate,
        limit,
        offset,
        normalizedSearch,
        sort.column,
        sort.order,
        controller.signal
      );

      clearTimeout(timeoutId);

      const mappedData = (response?.payroll || []).map(
        (record: any, index: number) => mapApiPayrollToRecord(record, index)
      );

      if (controller.signal.aborted) return;

      dispatch(setRecords(mappedData));
      dispatch(setIncentives(response?.incentives || []));
      dispatch(setTotal(response?.total || 0));

      setRealCheckRecords([]);
    } catch (error) {
      if (controller.signal.aborted || isPayrollFetchAborted(error)) {
        return;
      }
      console.log(error);
      notification("Error ⚠️", "Error al obtener las dependencias", "danger");
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (!controller.signal.aborted) {
        setLoading(false);
      }
      if (fetchAbortRef.current === controller) {
        fetchAbortRef.current = null;
      }
    }
  };

  const startDateKey = dates.start.format("YYYY-MM-DD");
  const endDateKey = dates.end.format("YYYY-MM-DD");

  const partnerIdsKey = React.useMemo(
    () =>
      records
        .map((r) => r.partnerId)
        .filter(Boolean)
        .sort((a, b) => a - b)
        .join(","),
    [records]
  );

  React.useEffect(() => {
    dispatch(setPage(0));
  }, [branch, startDateKey, endDateKey, deferredSearch, sort]);

  React.useEffect(() => {
    fetchData();
    return () => {
      fetchAbortRef.current?.abort();
    };
  }, [branch, startDateKey, endDateKey, page, limit, deferredSearch, sort]);

  React.useEffect(() => {
    incidentBatchAbortRef.current?.abort();
    if (loading || !branch || !partnerIdsKey) {
      setIncidentCountsByPartnerId({});
      setIncidentBatchPending(false);
      return;
    }
    const ac = new AbortController();
    incidentBatchAbortRef.current = ac;
    const partnerIds = partnerIdsKey.split(",").map(Number);
    setIncidentBatchPending(true);
    findIncidentPartnerCountsBatch(
      branch,
      startDateKey,
      endDateKey,
      partnerIds,
      ac.signal
    )
      .then((map) => {
        if (!ac.signal.aborted) {
          setIncidentCountsByPartnerId(map);
        }
      })
      .catch((err) => {
        if (!ac.signal.aborted) {
          setIncidentCountsByPartnerId({});
        }
      })
      .finally(() => {
        if (!ac.signal.aborted) {
          setIncidentBatchPending(false);
        }
      });
    return () => {
      ac.abort();
      setIncidentBatchPending(false);
    };
  }, [branch, startDateKey, endDateKey, loading, partnerIdsKey]);

  const isCuadroDataLoading =
    loading ||
    !rowDetailsBatchSettled ||
    (Boolean(partnerIdsKey) && incidentBatchPending);

  React.useEffect(() => {
    if (isCuadroDataLoading) {
      setLoadToastPhase("loading");
      return;
    }
    setLoadToastPhase((prev) => (prev === "loading" ? "done" : prev));
  }, [isCuadroDataLoading]);

  React.useEffect(() => {
    if (loadToastPhase !== "done") return;
    const hideTimer = window.setTimeout(() => setLoadToastPhase(null), 4200);
    return () => window.clearTimeout(hideTimer);
  }, [loadToastPhase]);

  React.useLayoutEffect(() => {
    if (partnerIdsKey && !loading) {
      setRowDetailsBatchSettled(false);
    }
  }, [partnerIdsKey, loading]);

  React.useEffect(() => {
    rowDetailsBatchAbortRef.current?.abort();

    if (loading || !branch || !partnerIdsKey) {
      setRowDetailsByPartnerId({});
      setRowDetailsBatchSettled(true);
      return;
    }

    const partnerIds = partnerIdsKey.split(",").map(Number);
    const needsBatchIds = records
      .filter((r) => {
        const embedded = (r as { snapshotDetail?: unknown }).snapshotDetail;
        return r.partnerId && !embedded;
      })
      .map((r) => r.partnerId);

    if (needsBatchIds.length === 0) {
      setRowDetailsByPartnerId({});
      setRowDetailsBatchSettled(true);
      return;
    }

    setRowDetailsBatchSettled(false);
    const ac = new AbortController();
    rowDetailsBatchAbortRef.current = ac;

    findPayrollRowDetailsBatch(
      branch,
      startDateKey,
      endDateKey,
      needsBatchIds,
      ac.signal,
      {
        onChunk: (partial) => {
          if (!ac.signal.aborted) {
            setRowDetailsByPartnerId((prev) => ({ ...prev, ...partial }));
          }
        },
      }
    )
      .catch((err) => {
        if (!ac.signal.aborted && !isPayrollFetchAborted(err)) {
          console.error("row-details-batch:", err);
        }
      })
      .finally(() => {
        if (!ac.signal.aborted) {
          setRowDetailsBatchSettled(true);
        }
      });

    return () => ac.abort();
  }, [branch, startDateKey, endDateKey, loading, partnerIdsKey]);

  return (
    <React.Fragment>
      <CuadroMandoLoadToast phase={loadToastPhase} />
      {/* Search input and Title */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5
            style={{
              color: "#374151",
              fontSize: "1.25rem",
              fontWeight: 600,
              margin: 0,
              marginBottom: "0.25rem",
            }}
          >
            Cuadro de mando
          </h5>
          <p
            style={{
              color: "#6b7280",
              fontSize: "0.875rem",
              fontWeight: 400,
              margin: 0,
            }}
          >
            Gestión y control de nómina
          </p>
        </div>
        <div className="d-flex align-items-center" style={{ gap: "0.5rem" }}>
          <SearchNomina
            placeholder="Buscar colaborador..."
            delayMs={300}
            onChangeDebounced={(val) => startTransition(() => setSearch(val))}
            onClearImmediate={() => startTransition(() => setSearch(""))}
          />
          {/* <button
            type="button"
            className="border-0 d-flex align-items-center"
            onClick={() => setShowAddSpecial(true)}
            title="Agregar colaborador con fechas especiales"
            style={{
              background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
              color: "#ffffff",
              borderRadius: "12px",
              padding: "0.35rem 0.75rem",
              fontSize: "0.75rem",
              fontWeight: 500,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            <i className="fas fa-plus me-1" style={{ fontSize: "0.7rem" }}></i>
            Especial
          </button> */}
        </div>
      </div>
      <div
        className="my-3"
        style={{
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
          border: "1px solid #e5e7eb",
        }}
      >
        {!loading && filteredRecords.length === 0 ? (
          <div className="d-flex align-items-center justify-content-center py-4 bg-white">
            <div className="d-flex flex-column align-items-center justify-content-center">
              <EmptyImage className="mx-auto w-250px" />
              <h5 className="text-secondary text-center fs-20px mt-3">
                No se encontraron registros
              </h5>
            </div>
          </div>
        ) : (
          <div
            className="invisible-scroll"
            style={{
              maxHeight: "65vh",
              overflowY: "auto",
              overflowX: "auto",
            }}
          >
            <table
              className="table table-hover text-nowrap align-middle mb-0 bg-white"
              style={{ fontSize: "0.8rem" }}
            >
              <PayrollHeader
                setSort={(column: string, order: string) =>
                  setSort({ column, order })
                }
                sort={sort}
              />
              <tbody>
                {loading && <CuadroMandoTableLoading colSpan={19} />}
                {!loading &&
                  filteredRecords.length > 0 &&
                  filteredRecords.map((schedule) => (
                    <React.Fragment
                      key={
                        schedule?.id || schedule?.partnerId || schedule?.name
                      }
                    >
                      <RowPayRoll
                        record={schedule}
                        dates={dates}
                        branch={branch}
                        fullDayHours={fullDayHours}
                        payrollBranchMeta={payrollBranchMeta}
                        incidentCountSnapshot={
                          incidentCountsByPartnerId[schedule.partnerId] ?? null
                        }
                        rowDetailsSnapshot={
                          rowDetailsByPartnerId[schedule.partnerId] ?? null
                        }
                        rowDetailsAwaitingBatch={!rowDetailsBatchSettled}
                        detailedRecords={detailedRecords}
                        realCheckRecords={realCheckRecords}
                        onRefreshPartner={refreshSinglePartner}
                        onCuadroExportSnapshot={onCuadroExportSnapshot}
                      />
                    </React.Fragment>
                  ))}
                {(specialPartners || []).map((sp, idx) => (
                  <React.Fragment key={`special-${sp.record.partnerId}-${idx}`}>
                    <RowPayRoll
                      record={sp.record}
                      dates={sp.customDates}
                      branch={branch}
                      fullDayHours={fullDayHours}
                      payrollBranchMeta={payrollBranchMeta}
                      incidentCountSnapshot={
                        incidentCountsByPartnerId[sp.record.partnerId] ?? null
                      }
                      rowDetailsSnapshot={
                        rowDetailsByPartnerId[sp.record.partnerId] ?? null
                      }
                      rowDetailsAwaitingBatch={!rowDetailsBatchSettled}
                      detailedRecords={[]}
                      realCheckRecords={[]}
                      isSpecial={true}
                      onRefreshPartner={undefined}
                      onCuadroExportSnapshot={onCuadroExportSnapshot}
                    />
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {!loading && records.length > 0 && (
        <div className="mt-3">
          <PaginationPayroll />
        </div>
      )}
      <ModalContainer
        open={showAddSpecial}
        title="Agregar colaborador especial"
        closeFn={() => {
          setShowAddSpecial(false);
          setSpecialSearchTerm("");
          setSpecialSelectedPartner(null);
          setSpecialDates(null);
          setSpecialSearchResults([]);
        }}
      >
        <div style={{ padding: "1rem", minWidth: "400px" }}>
          <div className="mb-3">
            <label className="form-label fw-semibold">Colaborador</label>
            <div ref={specialInputContainerRef} style={{ position: "relative" }}>
              <input
                type="text"
                className="form-control"
                placeholder="Buscar por nombre..."
                value={specialSelectedPartner
                  ? getPartnerDisplayName(specialSelectedPartner)
                  : specialSearchTerm}
                onChange={(e) => handleSpecialPartnerSearch(e.target.value)}
                onFocus={handleSpecialPartnerFocus}
                onBlur={() => setTimeout(() => setShowSpecialDropdown(false), 150)}
              />
              {showSpecialDropdown && specialSearchResults.length > 0 && (
                <div style={{
                  position: "fixed",
                  top: specialDropdownPos.top,
                  left: specialDropdownPos.left,
                  width: specialDropdownPos.width,
                  background: "white",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                  zIndex: 9999,
                  maxHeight: "220px",
                  overflowY: "auto",
                }}>
                  {specialSearchResults.map((partner) => (
                    <div
                      key={partner.id}
                      style={{ padding: "0.5rem 0.75rem", cursor: "pointer", fontSize: "0.85rem", display: "flex", alignItems: "center", gap: "0.5rem" }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = "#f8fafc"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "white"; }}
                      onClick={() => {
                        setSpecialSelectedPartner(partner);
                        setShowSpecialDropdown(false);
                      }}
                    >
                      <span>{getPartnerDisplayName(partner)}</span>
                      {newPartnerIds.has(partner.id) && (
                        <span style={{
                          fontSize: "0.65rem",
                          fontWeight: 600,
                          background: "#dcfce7",
                          color: "#16a34a",
                          borderRadius: "999px",
                          padding: "1px 7px",
                          whiteSpace: "nowrap",
                        }}>
                          Nuevo
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="mb-3">
            <label className="form-label fw-semibold">Rango de fechas</label>
            <RangePicker
              format="DD [de] MMMM [de] YYYY"
              placeholder={["Fecha de inicio", "Fecha de fin"]}
              size="large"
              allowClear={false}
              locale={locale}
              style={{ width: "100%", borderRadius: "8px" }}
              onChange={(newDates: any) => {
                if (newDates && newDates[0] && newDates[1]) {
                  setSpecialDates({ start: newDates[0], end: newDates[1] });
                }
              }}
            />
          </div>
          <div className="d-flex justify-content-end" style={{ gap: "0.5rem" }}>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => {
                setShowAddSpecial(false);
                setSpecialSearchTerm("");
                setSpecialSelectedPartner(null);
                setSpecialDates(null);
                setSpecialSearchResults([]);
              }}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-sm"
              disabled={!specialSelectedPartner || !specialDates || addingSpecial}
              onClick={handleAddSpecialPartner}
              style={{
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                color: "white",
                border: "none",
              }}
            >
              {addingSpecial ? "Agregando..." : "Agregar"}
            </button>
          </div>
        </div>
      </ModalContainer>
    </React.Fragment>
  );
};

export default PayrollBranch;

const Columns = [
  "Chilito",
  "Puesto",
  "Sueldo base 15",
  "Salario diario",
  "Pago por hora",
  "Días checados",
  "Jornadas completadas",
  "Horas trabajadas",
  "Turnos dobles",
  "Horas extras",
  "Pago general",
  "Descanso trabajado",
  "Bonos",

  "Días festivos",

  "Total de deducciones",

  "Incidencias",
  "Reloj checador",

  "Pago estimado",
];
